import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {DEFAULT_PROFILES, DEFAULT_VARS} from '../contentGenerator/constants';
import {useProducts} from '../contentGenerator/hooks/useProducts';
import {reconcileProductVars} from '../contentGenerator/services/products/productService';
import {buildFilteredGroupKey} from './reviewGrouping';

const ProductContext = createContext(null);

const defaultProfileSnapshot = DEFAULT_PROFILES[0];

export function ProductProvider({children}) {
    const initialManagedProductVarNames = DEFAULT_VARS.map((v) => v.name);
    const [collectionId, setCollectionId] = useState('');
    // Default to NO selection — the user explicitly picks SKUs. The
    // validation effect below tolerates an empty selection (it only falls
    // back to the first product when the current selection has stale IDs
    // that no longer exist), so starting empty is safe.
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [currentProductIndex, setCurrentProductIndex] = useState(0);
    const [vars, setVars] = useState(DEFAULT_VARS);
    const [managedProductVarNames, setManagedProductVarNames] = useState(initialManagedProductVarNames);
    const [resultsByProductId, setResultsByProductId] = useState({});
    const [reviewGroupByProductId, setReviewGroupByProductId] = useState({});
    const [activeProfileSnapshot, setActiveProfileSnapshot] = useState(defaultProfileSnapshot);
    const clearAllResults = useCallback(() => setResultsByProductId({}), []);
    const managedProductVarNamesRef = useRef(new Set(initialManagedProductVarNames));
    const appliedProductKeyRef = useRef('');

    const {
        products,
        productOptions,
        isLoading: productsLoading,
        error: productsError,
        source: productSource,
        lastSyncedAt,
        syncProducts,
    } = useProducts(collectionId);

    const selectedProducts = selectedProductIds
        .map((pid) => products.get(pid))
        .filter(Boolean);
    const currentProduct = selectedProducts[currentProductIndex] || selectedProducts[0] || null;

    const handleProductChange = useCallback((nextIds) => {
        const currentId = currentProduct?.id;
        const nextIndex = currentId ? nextIds.indexOf(currentId) : -1;
        setSelectedProductIds(nextIds);
        setCurrentProductIndex(nextIndex >= 0 ? nextIndex : 0);
    }, [currentProduct?.id]);

    const setReviewGroupForProducts = useCallback((productIds = [], superSku = '') => {
        const validProductIds = productIds.filter(Boolean);
        if (!validProductIds.length) return;

        setReviewGroupByProductId((currentGroups) => {
            const nextGroups = {...currentGroups};
            validProductIds.forEach((productId) => {
                nextGroups[productId] = superSku;
            });
            return nextGroups;
        });
    }, []);

    const replaceReviewWorkspace = useCallback(({
        productIds = [],
        reviewGroupByProductId: nextReviewGroupByProductId = {},
    } = {}) => {
        const nextProductIds = Array.from(new Set(productIds.filter(Boolean)));
        setSelectedProductIds(nextProductIds);
        setCurrentProductIndex(0);
        setReviewGroupByProductId({...nextReviewGroupByProductId});
        setResultsByProductId({});
    }, []);

    const groupFilteredProducts = useCallback(({filterText = '', productIds = []}) => {
        const validProductIds = productIds.filter(Boolean);
        if (validProductIds.length < 2) return '';

        const targetProducts = validProductIds
            .map((productId) => products.get(productId))
            .filter(Boolean);
        if (targetProducts.length < 2) return '';

        const nextGroupKey = buildFilteredGroupKey({
            items: targetProducts.map((product) => ({
                sku: product.sku || product.id,
                superSku: reviewGroupByProductId[product.id] || '',
            })),
            filterText,
            existingGroupKeys: Object.values(reviewGroupByProductId),
        });

        setReviewGroupForProducts(
            targetProducts.map((product) => product.id),
            nextGroupKey,
        );

        return nextGroupKey;
    }, [products, reviewGroupByProductId, setReviewGroupForProducts]);

    // Keep selection valid when product list changes
    useEffect(() => {
        if (!productOptions.length) return;
        const valid = selectedProductIds.filter((pid) => products.has(pid));
        if (!valid.length) {
            if (!selectedProductIds.length) {
                setCurrentProductIndex(0);
                return;
            }
            setSelectedProductIds([productOptions[0].id]);
            setCurrentProductIndex(0);
            return;
        }
        if (valid.length !== selectedProductIds.length) {
            setSelectedProductIds(valid);
        }
    }, [productOptions, products, selectedProductIds]);

    // Keep index in bounds
    useEffect(() => {
        if (!selectedProducts.length) return;
        if (currentProductIndex >= selectedProducts.length) {
            setCurrentProductIndex(selectedProducts.length - 1);
        }
    }, [currentProductIndex, selectedProducts]);

    // Reconcile vars when product changes
    useEffect(() => {
        if (!currentProduct) return;
        const key = `${collectionId}:${productSource}:${currentProduct.id}`;
        if (appliedProductKeyRef.current === key) return;
        appliedProductKeyRef.current = key;

        const productVars = currentProduct.vars?.length ? currentProduct.vars : DEFAULT_VARS;
        setVars((cur) => reconcileProductVars(cur, productVars, Array.from(managedProductVarNamesRef.current)));

        const nextManagedProductVarNames = new Set(managedProductVarNamesRef.current);
        productVars.forEach((variable) => nextManagedProductVarNames.add(variable.name));
        managedProductVarNamesRef.current = nextManagedProductVarNames;
        setManagedProductVarNames(Array.from(nextManagedProductVarNames));
    }, [collectionId, currentProduct, productSource]);

    const value = useMemo(() => ({
        collectionId, setCollectionId,
        products, productOptions,
        productsLoading, productsError,
        productSource, lastSyncedAt,
        syncProducts,
        selectedProductIds, setSelectedProductIds,
        currentProductIndex, setCurrentProductIndex,
        selectedProducts, currentProduct,
        handleProductChange,
        vars, setVars,
        managedProductVarNames,
        activeProfileSnapshot, setActiveProfileSnapshot,
        reviewGroupByProductId,
        setReviewGroupForProducts,
        replaceReviewWorkspace,
        groupFilteredProducts,
        resultsByProductId, setResultsByProductId, clearAllResults,
    }), [
        collectionId,
        products, productOptions,
        productsLoading, productsError,
        productSource, lastSyncedAt,
        syncProducts,
        selectedProductIds, currentProductIndex,
        selectedProducts, currentProduct,
        handleProductChange,
        vars,
        managedProductVarNames,
        activeProfileSnapshot,
        reviewGroupByProductId,
        setReviewGroupForProducts,
        replaceReviewWorkspace,
        groupFilteredProducts,
        resultsByProductId, clearAllResults,
    ]);

    return (
        <ProductContext.Provider value={value}>
            {children}
        </ProductContext.Provider>
    );
}

export function useProductContext() {
    const ctx = useContext(ProductContext);
    if (!ctx) throw new Error('useProductContext must be used within ProductProvider');
    return ctx;
}
